import { useOrderWorkLogs } from '@hooks';
import { isGranted } from '@lib';
import { OrderStatus } from '@models';
import { Order, OrderWorkLog } from '@services';
import { useStore } from '@stores';
import { message } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import Presentation from './OrderWorkLogs.presentation';

interface IProps {
  order: Order;
}

export const OrderWorkLogs: React.FC<IProps> = observer(({ order }) => {
  const { orderStore, authenticationStore } = useStore();
  const workLogsHook = useOrderWorkLogs(order.id);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<OrderWorkLog>();

  const currentUserId = authenticationStore.currentUser?.id;
  const canManage = isGranted('Pages.MyGarage.Manager');
  const locked = order.status === OrderStatus.Paid || order.status === OrderStatus.Ready;

  const canEdit = useCallback(
    (log: OrderWorkLog) => !locked && (canManage || log.userId === currentUserId),
    [locked, canManage, currentUserId]
  );

  const openCreate = useCallback(() => {
    setEditing(undefined);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((log: OrderWorkLog) => {
    setEditing(log);
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditing(undefined);
  }, []);

  const handleDelete = useCallback(
    async (log: OrderWorkLog) => {
      await orderStore.deleteOrderWorkLog(log.id);
      message.success('Time entry removed');
    },
    [orderStore]
  );

  return (
    <Presentation
      order={order}
      workLogsHook={workLogsHook}
      locked={locked}
      canManage={canManage}
      currentUserId={currentUserId}
      canEdit={canEdit}
      editing={editing}
      formOpen={formOpen}
      openCreate={openCreate}
      openEdit={openEdit}
      closeForm={closeForm}
      handleDelete={handleDelete}
    />
  );
});
